import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useMemo, useState } from 'react';
import { Accept } from 'react-dropzone';
import styled from 'styled-components';

import { Checkbox } from '@creditornot/cb-checkbox';
import { Modal } from '@creditornot/cbt-modal';
import { Button } from 'common/components/cookbook/Button';
import { FileInput } from 'common/components/cookbook/FileInput';
import { ModalFooter } from 'common/components/cookbook/Modals/ModalFooter';
import { Stack } from 'common/components/cookbook/Stack';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { useSelectedCountryCodes } from 'common/hooks/useSelectedCountryCodes';
import { useToast } from 'common/hooks/useToast';
import { useApiQueryClient } from 'common/query/useApiQueryClient';
import { bulkUploadOverrideCsv } from 'features/override-management/api';
import { BulkOperation } from 'features/override-management/api-types';
import { BULK_OPERATIONS_QUERY_KEY_ROOT } from 'features/override-management/query';

import { ExampleFileDownloadButton } from '../UploadOverrideCSVModal/ExampleFileDownloadButton';
import OverrideFileFormat from '../UploadOverrideCSVModal/OverrideFileFormat';

type Props = {
  isOpen: boolean;
  onSuccess: (operation: BulkOperation) => void;
  onDismiss: () => void;
};

const Content = styled(Stack)`
  width: 100%;
`;

const FILE_INPUT_ACCEPT_CSV: Accept = {
  'text/csv': ['.csv'],
};

export function BulkUploadOverridesModal({ isOpen, onDismiss, onSuccess }: Props) {
  const [files, setFiles] = useState<File[]>([]);
  const [deleteExisting, setDeleteExisting] = useState(false);

  const format = useFormatMessage();
  const [countryCode] = useSelectedCountryCodes();
  const { addErrorToast } = useToast();
  const queryClient = useApiQueryClient();

  const { mutate: uploadCsv, isPending: uploadCsvPending } = useMutation<
    BulkOperation,
    AxiosError,
    { file: File; deleteExistingOverrides: boolean }
  >({
    mutationFn: ({ file, deleteExistingOverrides }) =>
      bulkUploadOverrideCsv(countryCode, file, deleteExistingOverrides),
    onSuccess: (result) => {
      onSuccess(result);
      void queryClient.invalidateQueries({
        queryKey: [BULK_OPERATIONS_QUERY_KEY_ROOT, countryCode],
      });
      resetState();
    },
    onError: addErrorToast,
  });

  function resetState() {
    setFiles([]);
    setDeleteExisting(false);
  }

  function handleDismiss() {
    onDismiss();
    resetState();
  }

  function handleUploadButtonClick() {
    uploadCsv({ file: files[0], deleteExistingOverrides: deleteExisting });
  }

  const fileNames = useMemo(() => files.map((file) => file.name), [files]);

  return (
    <Modal
      sizePreset="centered:large"
      open={isOpen}
      onDismiss={handleDismiss}
      title={format('common.upload_csv_file')}
      FooterComponent={ModalFooter}
      footer={
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          gap="regularMedium"
          style={{ width: '100%' }}
        >
          <Checkbox
            checked={deleteExisting}
            onChange={() => setDeleteExisting(!deleteExisting)}
            style={{ flexShrink: 0 }}
          >
            {format('forecasting.delete_overrides_not_in_the_file')}
          </Checkbox>
          <Stack
            direction="row"
            gap="regularMedium"
            alignItems="center"
            justifyContent="flex-end"
            style={{ width: '100%' }}
          >
            <Button onClick={handleDismiss}>{format('common.cancel')}</Button>
            <Button
              variant="primary"
              onClick={handleUploadButtonClick}
              loading={uploadCsvPending}
              disabled={uploadCsvPending || files.length === 0}
            >
              {format('common.upload')}
            </Button>
          </Stack>
        </Stack>
      }
    >
      <Content gap="regularLarge">
        <ExampleFileDownloadButton />
        <OverrideFileFormat />
        <FileInput
          onDrop={setFiles}
          id="bulkOverridesCsv"
          accept={FILE_INPUT_ACCEPT_CSV}
          selectedFiles={fileNames}
          buttonText={format('common.choose_csv_file')}
        />
      </Content>
    </Modal>
  );
}
